package com.matchpredictor.web

import com.matchpredictor.web.api.ApiClient
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLTextAreaElement

private const val CHAT_PAGE_SIZE = 10
private const val CHAT_MESSAGE_LIMIT = 1000
private val chatScope = MainScope()

fun chatPage(): HTMLElement {
    val section = el("section", "page chat-page")
    section.appendChild(el("h1", text = "Общий чат"))
    section.appendChild(el("p", "lede", "Обсуждаем матчи, отвечаем друг другу и кидаем гусиные стикеры."))

    val panel = el("div", "chat-panel")
    val toolbar = el("div", "chat-toolbar")
    val connectionStatus = el("span", "chat-status", "Подключение…")
    toolbar.appendChild(connectionStatus)

    val list = el("div", "chat-messages edge-fade-y")
    val empty = el("p", "muted chat-empty", "Сообщений пока нет. Начните разговор.")
    val replyPreview = el("div", "chat-reply-preview")
    val stickerPicker = el("div", "sticker-picker")
    stickerPicker.hidden = true
    val composer = el("div", "chat-composer")
    val textarea = document.createElement("textarea") as HTMLTextAreaElement
    textarea.className = "input chat-input"
    textarea.placeholder = "Напишите сообщение"
    textarea.maxLength = CHAT_MESSAGE_LIMIT
    textarea.rows = 2
    val composerSide = el("div", "chat-composer-side")
    val counter = el("span", "chat-counter", "0/$CHAT_MESSAGE_LIMIT")
    val composerActions = el("div", "chat-composer-actions")
    val stickerBtn = el("button", "ghost chat-sticker-btn")
    stickerBtn.setAttribute("type", "button")
    stickerBtn.setAttribute("aria-label", "Стикеры")
    stickerBtn.setAttribute("title", "Стикеры")
    stickerBtn.setAttribute("aria-pressed", "false")
    val stickerBtnIcon = document.createElement("img") as HTMLImageElement
    stickerBtnIcon.className = "chat-sticker-btn-icon"
    stickerBtnIcon.src = stickerImageUrl("nu_gus")
    stickerBtnIcon.alt = ""
    stickerBtn.appendChild(stickerBtnIcon)
    val sendButton = button("Отправить", "primary") {}
    val formError = el("p", "form-error chat-error")
    composerActions.appendChild(stickerBtn)
    composerActions.appendChild(sendButton)
    composerSide.appendChild(counter)
    composerSide.appendChild(composerActions)
    composer.appendChild(textarea)
    composer.appendChild(composerSide)

    panel.appendChild(toolbar)
    panel.appendChild(list)
    panel.appendChild(replyPreview)
    panel.appendChild(stickerPicker)
    panel.appendChild(composer)
    panel.appendChild(formError)
    section.appendChild(panel)

    val messages = mutableMapOf<Long, ChatMessageDto>()
    var replyTo: ChatMessageDto? = null
    var loadingOlder = false
    var hasOlder = true
    var stickers: List<StickerDto> = emptyList()
    var chatMessageCount = 0
    lateinit var afterSend: (ChatMessageDto) -> Unit

    fun isNearBottom(): Boolean =
        list.scrollHeight - list.scrollTop - list.clientHeight < 80

    fun previewBody(message: ChatMessageDto): String =
        if (message.kind == ChatMessageKind.STICKER) "Стикер: ${message.body}" else message.body.take(180)

    fun renderReplyPreview() {
        replyPreview.textContent = ""
        val message = replyTo
        if (message == null) {
            replyPreview.style.display = "none"
            return
        }
        replyPreview.style.display = "flex"
        val text = el("div")
        text.appendChild(el("strong", text = "Ответ для ${message.displayName}"))
        text.appendChild(el("span", "muted", previewBody(message)))
        replyPreview.appendChild(text)
        replyPreview.appendChild(button("Отменить", "ghost chat-cancel-reply") {
            replyTo = null
            renderReplyPreview()
        })
    }

    fun renderMessages(scrollToBottom: Boolean = false, preserveOlderPosition: Pair<Int, Double>? = null) {
        list.textContent = ""
        if (messages.isEmpty()) {
            list.appendChild(empty)
            bindScrollEdgeFade(list, EdgeFadeAxis.VERTICAL)
            return
        }
        messages.values.sortedBy { it.id }.forEach { message ->
            val card = el("article", "chat-message")
            card.id = "chat-message-${message.id}"
            val head = el("div", "chat-message-head")
            head.appendChild(chatAvatar(message.avatarIcon))
            head.appendChild(el("strong", "chat-author", message.displayName))
            head.appendChild(el("time", "chat-time", formatChatTime(message.createdAt)))
            card.appendChild(head)

            message.replyTo?.let { reply ->
                val quote = el("blockquote", "chat-quote")
                quote.appendChild(el("strong", text = reply.displayName))
                quote.appendChild(el("span", text = reply.body))
                card.appendChild(quote)
            }
            if (message.kind == ChatMessageKind.STICKER && !message.stickerCode.isNullOrBlank()) {
                val img = document.createElement("img") as HTMLImageElement
                img.className = "chat-sticker"
                img.src = stickerImageUrl(message.stickerCode!!)
                img.alt = message.body
                img.title = message.body
                card.appendChild(img)
            } else {
                card.appendChild(el("p", "chat-body", message.body))
            }
            card.appendChild(button("Ответить", "ghost chat-reply") {
                replyTo = message
                renderReplyPreview()
                textarea.focus()
            })
            list.appendChild(card)
        }
        when {
            preserveOlderPosition != null -> {
                val (oldHeight, oldTop) = preserveOlderPosition
                list.scrollTop = oldTop + (list.scrollHeight - oldHeight)
            }
            scrollToBottom -> list.scrollTop = list.scrollHeight.toDouble()
        }
        bindScrollEdgeFade(list, EdgeFadeAxis.VERTICAL)
    }

    fun mergeMessages(items: List<ChatMessageDto>, scrollToBottom: Boolean, prepend: Boolean = false) {
        val oldPosition = if (prepend) list.scrollHeight to list.scrollTop else null
        items.forEach { messages[it.id] = it }
        renderMessages(scrollToBottom, oldPosition)
    }

    fun renderStickerPicker() {
        stickerPicker.textContent = ""
        if (stickers.isEmpty()) {
            stickerPicker.appendChild(el("p", "muted", "Стикеры загружаются…"))
            return
        }
        val grid = el("div", "sticker-picker-grid")
        stickers.forEach { sticker ->
            val cell = el("button", if (sticker.unlocked) "sticker-cell" else "sticker-cell locked")
            cell.setAttribute("type", "button")
            cell.setAttribute("title", if (sticker.unlocked) sticker.title else "С ${sticker.unlockAt} сообщений")
            val img = document.createElement("img") as HTMLImageElement
            img.src = stickerImageUrl(sticker.icon)
            img.alt = sticker.title
            img.className = "sticker-cell-img"
            cell.appendChild(img)
            if (!sticker.unlocked) {
                val need = (sticker.unlockAt - chatMessageCount).coerceAtLeast(0)
                cell.appendChild(el("span", "sticker-lock", "ещё $need"))
            } else {
                cell.addEventListener("click", {
                    formError.textContent = ""
                    val replyId = replyTo?.id
                    chatScope.launch {
                        runCatching { ApiClient.sendChatMessage(null, replyId, sticker.code) }
                            .onSuccess { afterSend.invoke(it) }
                            .onFailure { formError.textContent = errorMessage(it) }
                    }
                })
            }
            grid.appendChild(cell)
        }
        stickerPicker.appendChild(grid)
        stickerPicker.appendChild(
            el("p", "muted sticker-progress", "Сообщений: $chatMessageCount")
        )
    }

    fun refreshStickers() {
        chatScope.launch {
            runCatching { ApiClient.stickersMine() }
                .onSuccess { res ->
                    stickers = res.items
                    chatMessageCount = res.chatMessageCount
                    if (!stickerPicker.hidden) renderStickerPicker()
                }
        }
    }

    fun setStickerPickerOpen(open: Boolean) {
        stickerPicker.hidden = !open
        stickerBtn.classList.toggle("is-active", open)
        stickerBtn.setAttribute("aria-pressed", if (open) "true" else "false")
        if (open) {
            renderStickerPicker()
            refreshStickers()
        }
    }

    afterSend = { message ->
        messages[message.id] = message
        textarea.value = ""
        counter.textContent = "0/$CHAT_MESSAGE_LIMIT"
        replyTo = null
        renderReplyPreview()
        renderMessages(scrollToBottom = true)
        setStickerPickerOpen(false)
        refreshStickers()
    }

    fun syncLatest() {
        chatScope.launch {
            runCatching { ApiClient.chatHistory(limit = CHAT_PAGE_SIZE) }
                .onSuccess { items ->
                    val shouldScroll = messages.isEmpty() || isNearBottom()
                    mergeMessages(items, shouldScroll)
                }
                .onFailure { formError.textContent = errorMessage(it) }
        }
    }

    fun loadOlderMessages() {
        if (loadingOlder || !hasOlder) return
        loadingOlder = true
        chatScope.launch {
            runCatching {
                ApiClient.chatHistory(messages.keys.minOrNull(), CHAT_PAGE_SIZE)
            }.onSuccess { items ->
                hasOlder = items.size == CHAT_PAGE_SIZE
                mergeMessages(items, scrollToBottom = false, prepend = true)
            }.onFailure {
                formError.textContent = errorMessage(it)
            }
            loadingOlder = false
        }
    }

    fun sendMessage() {
        val body = textarea.value.trim()
        if (body.isEmpty()) {
            formError.textContent = "Введите сообщение"
            return
        }
        if (body.length > CHAT_MESSAGE_LIMIT) {
            formError.textContent = "Сообщение слишком длинное"
            return
        }
        sendButton.asDynamic().disabled = true
        formError.textContent = ""
        val replyId = replyTo?.id
        chatScope.launch {
            runCatching { ApiClient.sendChatMessage(body, replyId) }
                .onSuccess { afterSend(it) }
                .onFailure { formError.textContent = errorMessage(it) }
            sendButton.asDynamic().disabled = false
        }
    }

    stickerBtn.addEventListener("click", {
        setStickerPickerOpen(stickerPicker.hidden)
    })

    list.addEventListener("scroll", {
        if (list.scrollTop <= 40) loadOlderMessages()
    })
    sendButton.addEventListener("click", { sendMessage() })
    textarea.addEventListener("input", {
        counter.textContent = "${textarea.value.length}/$CHAT_MESSAGE_LIMIT"
    })
    textarea.addEventListener("keydown", { event ->
        val keyboard = event.asDynamic()
        if (keyboard.key == "Enter" && keyboard.shiftKey != true) {
            event.preventDefault()
            sendMessage()
        }
    })

    renderReplyPreview()
    refreshStickers()
    syncLatest()
    ChatStream.connect(
        onMessage = { message ->
            val shouldScroll = isNearBottom()
            messages[message.id] = message
            renderMessages(scrollToBottom = shouldScroll)
        },
        onConnectionChange = { connected ->
            connectionStatus.textContent = if (connected) "В сети" else "Переподключение…"
            connectionStatus.className = if (connected) "chat-status connected" else "chat-status"
        },
        onConnected = { syncLatest() }
    )
    return section
}

private fun chatAvatar(avatarIcon: String?): HTMLElement {
    val image = document.createElement("img") as HTMLImageElement
    image.className = "chat-avatar"
    image.src = achievementImageUrl(avatarIcon?.takeIf { it.isNotBlank() } ?: DEFAULT_AVATAR_ICON)
    image.alt = ""
    return image
}

/**
 * Formats chat timestamps in the viewer's local timezone.
 * Expects Instant ISO with `Z`/offset; bare LocalDateTime is treated as UTC for legacy rows.
 */
private fun formatChatTime(value: String): String {
    val normalized = when {
        value.isBlank() -> return value
        value.endsWith("Z", ignoreCase = true) -> value
        value.contains('+') || value.matches(Regex(""".*T\d{2}:\d{2}(:\d{2})?([.,]\d+)?-\d{2}:?\d{2}$""")) -> value
        value.contains('T') -> "${value.trimEnd('Z')}Z"
        else -> value
    }
    val ms = js("Date.parse")(normalized) as Double
    if (ms.isNaN()) return value
    return formatChatTimeFromMillis(ms)
}

private fun formatChatTimeFromMillis(ms: Double): String {
    val d = kotlin.js.Date(ms)
    fun pad(n: Int): String = if (n < 10) "0$n" else n.toString()
    val time = "${pad(d.getHours())}:${pad(d.getMinutes())}"
    val now = kotlin.js.Date()
    val sameDay = d.getFullYear() == now.getFullYear() &&
        d.getMonth() == now.getMonth() &&
        d.getDate() == now.getDate()
    return if (sameDay) {
        time
    } else {
        "${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} $time"
    }
}
